'use client';

import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { cn } from '@/lib/utils';

type Align = 'start' | 'end';

type Side = 'bottom' | 'top';

/**
 * Positions a `popover` element next to its trigger. CSS anchor positioning
 * is not Baseline yet (Safari), so the maths is done on the `toggle` event
 * with `position: fixed`; the top layer takes care of stacking.
 */
function usePopoverPosition(align: Align, side: Side, offset = 6) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<CSSProperties>({});
  const [openState, setOpenState] = useState(false);

  const place = useCallback(() => {
    const t = triggerRef.current;
    const p = panelRef.current;

    if (!t || !p) return;
    const r = t.getBoundingClientRect();
    const pw = p.offsetWidth;
    const ph = p.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = align === 'start' ? r.left : r.right - pw;
    left = Math.max(8, Math.min(left, vw - pw - 8));
    let top = side === 'bottom' ? r.bottom + offset : r.top - ph - offset;

    if (side === 'bottom' && top + ph > vh - 8 && r.top - ph - offset > 8) top = r.top - ph - offset;

    if (side === 'top' && top < 8) top = r.bottom + offset;
    setStyle({ position: 'fixed', left, top, margin: 0 });
  }, [align, side, offset]);

  useEffect(() => {
    if (!openState) return;
    place();
    const onChange = () => place();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);

    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [openState, place]);

  return { triggerRef, panelRef, style, openState, setOpenState, place };
}

export interface PopoverProps {
  /** A single button element; `popovertarget` and the ref are attached to it. */
  trigger: ReactNode;
  children: ReactNode;
  align?: Align;
  side?: Side;
  className?: string;
  /** `auto` light-dismisses; `manual` stays until closed. */
  mode?: 'auto' | 'manual';
  onOpenChange?: (open: boolean) => void;
}

/** Popover on the HTML `popover` attribute: light dismiss, Escape, top layer. */
export function Popover({ trigger, children, align = 'start', side = 'bottom', className, mode = 'auto', onOpenChange }: PopoverProps) {
  const id = useId();
  const { triggerRef, panelRef, style, setOpenState } = usePopoverPosition(align, side);

  if (!isValidElement<ButtonHTMLAttributes<HTMLButtonElement> & { ref?: unknown }>(trigger)) {
    throw new Error('Popover trigger must be a single button element');
  }

  // SAFETY: The surrounding typed contract establishes the asserted representation.
  const triggerEl = cloneElement(trigger, {
    ref: triggerRef,
    popoverTarget: id,
    'aria-haspopup': trigger.props['aria-haspopup'] ?? 'dialog',
  } as Record<string, unknown>);

  return (
    <>
      {triggerEl}
      <div
        id={id}
        ref={panelRef}
        popover={mode}
        style={style}
        onToggle={(e) => {
          // SAFETY: The surrounding typed contract establishes the asserted representation.
          const open = (e as unknown as ToggleEvent).newState === 'open';
          setOpenState(open);
          onOpenChange?.(open);
        }}
        className={cn(
          'min-w-40 rounded-card border border-line bg-surface p-1 text-sm text-ink',
          'shadow-[0_12px_32px_rgb(0_0_0/0.35),inset_0_1px_0_rgb(255_255_255/0.04)]',
          '[[data-theme=light]_&]:shadow-[0_12px_32px_rgb(0_0_0/0.12)]',
          'open:animate-[dialog-in_140ms_cubic-bezier(0.2,0,0,1)]',
          className
        )}
      >
        {children}
      </div>
    </>
  );
}

/** Closes the nearest open popover ancestor; use inside Menu items. */
export function closePopover(el: HTMLElement | null) {
  // SAFETY: The surrounding typed contract establishes the asserted representation.
  const p = el?.closest('[popover]') as HTMLElement | null;
  p?.hidePopover();
}
